import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { activeWorkPanelProjectIdAtom, workPanelProjectIdAtomFamily } from './workPanel';

/**
 * Switches a session's Work panel to an EXISTING project — the manual
 * counterpart to `useCreateSessionWorkProject` (which always makes a new
 * one). Used by `SessionWorkProjectSelector` when the user picks a project
 * out of this session's history, including recovering one after a hard
 * reload (`workPanelProjectIdAtomFamily` is in-memory only, so a refreshed
 * tab forgets which project was showing even though it still exists
 * server-side).
 *
 * Writes both the per-session atom (survives this panel closing/reopening)
 * and the active override (renders immediately, same as
 * `useCreateSessionWorkProject`'s `onCreated`) so `useWorkPanelProjectId`
 * picks it up without waiting on a refetch.
 */
export const useSelectWorkPanelProject = (
  sessionId: string
): ((projectId: string) => void) => {
  const setActiveWorkPanelProjectId = useSetAtom(activeWorkPanelProjectIdAtom);
  const setRecentWorkPanelProjectId = useSetAtom(workPanelProjectIdAtomFamily(sessionId));

  return useCallback(
    (projectId: string) => {
      setRecentWorkPanelProjectId(projectId);
      setActiveWorkPanelProjectId({ sessionId, projectId, inspectReadOnly: true });
    },
    [sessionId, setActiveWorkPanelProjectId, setRecentWorkPanelProjectId]
  );
};
